import React, { useState } from 'react';
import { Box, makeStyles, Tab, Tabs, TabsProps } from '@material-ui/core';
import { randomId } from '../../util/strings';
import { MultiPanelCardPanelProps } from './MultiPanelCardPanel';

const useStyles = makeStyles(theme => ({
    tab: {
        minHeight: 'auto',
        '& > span': {
            flexDirection: 'row',
        },
        '& svg': {
            marginRight: theme.spacing(1),
            marginBottom: 0,
        },
    },
    tabs: {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}));

export interface MultiPanelCardContentProps {
    currentTab?: string;
    onTabChange?: (currenTab: string) => void;
    children: React.ReactElement<MultiPanelCardPanelProps>[];
    TabsProps?: TabsProps;
}

export const MultiPanelCardContent: React.FunctionComponent<MultiPanelCardContentProps> = props => {
    const classes = useStyles({});
    const elementId = randomId(8);

    const tabData = React.Children.map(props.children, (child, i) => ({
        title: child.props.title,
        icon: child.props.icon,
        id: child.props.id || `${elementId}_${i}`,
    }));

    const initialTab = props.currentTab && tabData.findIndex(td => td.id === props.currentTab)
        ? props.currentTab
        : tabData.length > 0 ? tabData[0].id : null;

    const [ currentTab, setCurrentTab ] = useState<string>(initialTab);

    const panelId = id => `${id}_panel`;

    const handleTabChange = newTab => {
        if (props.onTabChange) {
            props.onTabChange(newTab);
        }
        setCurrentTab(newTab);
    };

    return (
        <>
            <Tabs
                value={currentTab}
                onChange={(e, v) => handleTabChange(v)}
                textColor="primary"
                variant="fullWidth"
                className={classes.tabs}
                {...props.TabsProps || {}}
            >
                {tabData.map(tab => (
                    <Tab
                        key={tab.id}
                        className={classes.tab}
                        value={tab.id}
                        label={tab.title}
                        id={tab.id}
                        icon={tab.icon}
                        aria-controls={panelId(tab.id)}
                    />
                ))}
            </Tabs>
            {React.Children.map(props.children, (child, i) => (
                <Box
                    id={panelId(tabData[i].id)}
                    aria-labelledby={tabData[i].id}
                    hidden={currentTab !== tabData[i].id}
                >
                    {child.props.children}
                </Box>
            ))}
        </>
    );
};
